import type { FloorDM } from '@Model/FloorModels';
import OwnableTheme from '@Theme';
import { Motion } from '@legendapp/motion';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import styled from 'styled-components/native';

import {
  OTHER_PAGES_SPACE_HEIGHT,
  useFloorMapSizer,
} from '../../FloorMapSizer';
import { MapPageContent } from './MapPageContent/MapPageContent';
import {
  MapPagePresenterContext,
  getMapPagePresenter,
} from './MapPagePresenter';

type Props = {
  floorId: FloorDM['id'];
};

export const MapPage = observer(function MapPage({ floorId }: Props) {
  const [presenter] = useState(() => getMapPagePresenter(floorId));
  const { animatedStyle, destroy, zIndex, relativeFloorLevel } = presenter;

  useEffect(
    () => () => {
      destroy();
    },
    []
  );

  const { pagesContainer } = useFloorMapSizer();
  const mapHeight = Math.max(
    0,
    pagesContainer.value.height - OTHER_PAGES_SPACE_HEIGHT * 2 + 60
  );

  // only display up to 4 pages below the current floor page
  if (relativeFloorLevel > 4) {
    return null;
  }

  return (
    <MapPagePresenterContext.Provider value={presenter}>
      <Motion.View
        key={String(floorId)}
        style={[styles.page, { zIndex }]}
        animate={animatedStyle}
        transition={{ type: 'spring' }}
      >
        <MapWrapper style={{ height: mapHeight }}>
          <MapPageContent />
        </MapWrapper>
      </Motion.View>
    </MapPagePresenterContext.Provider>
  );
});

const styles = StyleSheet.create({
  levelNumberWrapper: { position: 'absolute', right: 0 },
  levelNumberFont: {
    fontFamily: OwnableTheme.fonts.gilroy.black,
    color: OwnableTheme.colors.white,
    textShadowColor: '#AAA',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 1,
  },
  page: {
    width: '100%',
    position: 'absolute',
  },
});

const MapWrapper = styled.Pressable`
  padding: 5.42%;
  background: ${OwnableTheme.colors.white};
  box-shadow: 0 0 22px ${OwnableTheme.colors.modalShadow};
  border-radius: 15px;
  overflow: hidden;
  width: 100%;
`;
